import React from 'react';
import { styled } from '@linaria/react';
import { ChevronDown } from 'lucide-react';

export interface Props {
  id: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  children: React.ReactElement | Array<React.ReactElement>;
}

function Select({
  id,
  value,
  onChange,
  children,
  ...delegated
}: Props) {
  const displayedValue = getDisplayedValue(value, children);

  return (
    <Wrapper>
      <NativeSelect id={id} value={value} onChange={onChange}>
        {children}
      </NativeSelect>
      <PresentationalBit {...delegated}>
        {displayedValue}
        <IconWrapper style={{ '--size': 24 + 'px' }}>
          <ChevronDown size={24} />
        </IconWrapper>
      </PresentationalBit>
    </Wrapper>
  );
}

export function getDisplayedValue(
  value: string,
  children: React.ReactElement | Array<React.ReactElement>
) {
  const childArray = Array.isArray(children) ? children : [children];
  const selectedChild = childArray.find((child) => {
    return child.props.value === value;
  });

  if (!selectedChild) {
    throw new Error(
      `Could not find selected child with value: ${value}`
    );
  }

  return selectedChild.props.children;
}

const Wrapper = styled.div`
  position: relative;
`;

const NativeSelect = styled.select`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  font-size: 1rem;
  cursor: pointer;
  -webkit-appearance: none;
`;

const PresentationalBit = styled.div`
  color: var(--color-gray-900);
  background-color: var(--color-gray-100);
  font-size: ${16 / 16}rem;
  padding: 8px 12px;
  padding-right: 52px;
  border-radius: 8px;
  outline-offset: 2px;

  ${NativeSelect}:focus:not(:focus-visible) + & {
    outline: none;
  }

  ${NativeSelect}:focus-visible + & {
    outline: 1px dotted #212121;
    outline: 5px auto -webkit-focus-ring-color;
  }

  ${NativeSelect}:hover + & {
    color: var(--color-text);
    background-color: var(--color-gray-200);
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 10px;
  margin: auto;
  width: var(--size);
  height: var(--size);
  pointer-events: none;
`;

export default Select;
